import { memo } from "react";
import SmallStar from "@Icons/SmallStar";
import wrapperHOC from "@Utils/wrapperHOC";
import styles from "./Rating.module.scss";

interface StarRatingProps {
  /** Numeric rating (e.g. 4.3) */
  rating: number;
  /** Total review count shown in parentheses */
  reviewCount?: number;
  /** Additional className for the outer container */
  className?: string;
  /** data-testid for testing */
  testId?: string;
}

const StarRating = ({
  rating,
  reviewCount,
  className = "",
  testId = "star-rating",
}: StarRatingProps) => {
  // Clamp to 0–5 and guard against NaN/undefined to prevent RangeError in Array constructor
  const safeRating = Math.min(5, Math.max(0, Number(rating) || 0));
  const fullStars = Math.floor(safeRating);
  const hasPartial = safeRating % 1 !== 0;
  const emptyStars = 5 - Math.ceil(safeRating);

  return (
    <div
      className={`${styles.container} ${className}`}
      data-testid={testId}
      aria-label={`${safeRating} out of 5 stars${
        reviewCount != null ? `, ${reviewCount} reviews` : ""
      }`}
    >
      <span className={styles.ratingValue}>{safeRating.toFixed(1)}</span>
      <div className={styles.starContainer}>
        {[...new Array(fullStars)].map((_, i) => (
          <SmallStar key={`full-${i}`} />
        ))}
        {hasPartial && (
          <div className={styles.partialStarContainer}>
            <div className={styles.partialStar} style={{ width: `${(safeRating % 1) * 100}%` }}>
              <SmallStar />
            </div>
            <SmallStar color="#d1d5db" />
          </div>
        )}
        {[...new Array(emptyStars)].map((_, i) => (
          <SmallStar key={`empty-${i}`} color="#d1d5db" />
        ))}
      </div>
      {reviewCount != null && reviewCount > 0 && (
        <span className={styles.reviewCount}>({reviewCount})</span>
      )}
    </div>
  );
};

export default wrapperHOC(memo(StarRating), {
  componentName: "StarRating",
  showForChina: true,
});
